import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { FaLightbulb, FaGraduationCap, FaBuilding, FaHandshake } from 'react-icons/fa';

const Partenaire = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();

  const handleNavigate = () => {
    navigate('/partenaire');
    // Scroll vers le haut après navigation
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 100);
  };

  const styles = `
    .partner-section {
      padding: 96px 52px;
      background: #f8f9fa;
      position: relative;
      overflow: hidden;
    }

    .partner-section::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 1px;
      background: linear-gradient(90deg, transparent, #f4c430, transparent);
    }

    .partner-inner {
      max-width: 1320px;
      margin: 0 auto;
      text-align: center;
    }

    .partner-badge {
      display: inline-flex;
      align-items: center;
      gap: 10px;
      font-size: 14px;
      font-weight: 400;
      letter-spacing: 1px;
      text-transform: uppercase;
      color: #1a3a52;
      margin-bottom: 24px;
    }

    .partner-badge::before {
      content: '';
      display: block;
      width: 20px; height: 2px;
      background: #1a3a52;
    }

    .partner-title {
      font-family: Arial, sans-serif;
      font-size: clamp(1.5rem, 3vw, 2.2rem);
      font-weight: 600;
      color: #0f2537;
      line-height: 1.1;
      margin-bottom: 24px;
    }

    .partner-title em {
      font-style: normal;
      color: #f4c430;
    }

    .partner-subtitle {
      font-family: Arial, sans-serif;
      font-size: 16px;
      line-height: 1.7;
      color: #6b7280;
      max-width: 700px;
      margin: 0 auto 48px;
    }

    .partner-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
      gap: 32px;
      margin: 64px 0;
    }

    .partner-card {
      background: #ffffff;
      border-radius: 12px;
      padding: 32px 24px;
      text-align: center;
      transition: transform 0.3s ease, box-shadow 0.3s ease;
      border: 1px solid #e5e7eb;
      cursor: pointer;
    }

    .partner-card:hover {
      transform: translateY(-8px);
      box-shadow: 0 20px 40px rgba(244, 196, 48, 0.15);
      border-color: #f4c430;
    }

    .partner-icon {
      font-size: 3rem;
      margin-bottom: 20px;
      display: block;
    }

    .partner-card-title {
      font-family: Arial, sans-serif;
      font-size: 18px;
      font-weight: 600;
      color: #0f2537;
      margin-bottom: 12px;
    }

    .partner-card-desc {
      font-family: Arial, sans-serif;
      font-size: 14px;
      line-height: 1.6;
      color: #6b7280;
    }

    .partner-cta {
      margin-top: 48px;
    }

    .partner-button {
      background: #1a3a52;
      color: #ffffff;
      border: none;
      padding: 16px 40px;
      border-radius: 6px;
      font-family: Arial, sans-serif;
      font-size: 14px;
      font-weight: 600;
      letter-spacing: 0.5px;
      cursor: pointer;
      transition: background 0.2s, transform 0.2s, box-shadow 0.2s;
      display: inline-flex;
      align-items: center;
      gap: 10px;
    }

    .partner-button:hover {
      background: #f4c430;
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(244, 196, 48, 0.35);
    }

    .partner-arrow {
      transition: transform 0.2s;
    }

    .partner-button:hover .partner-arrow {
      transform: translateX(4px);
    }

    @media (max-width: 960px) {
      .partner-section { padding: 64px 24px; }
      .partner-grid { grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 24px; }
    }

    @media (max-width: 640px) {
      .partner-title { font-size: 2rem; }
      .partner-card { padding: 24px 20px; }
    }
  `;

  const partnerTypes = [
    {
      icon: <FaLightbulb />,
      title: 'Experts & Consultants',
      description: 'Rejoignez notre réseau d\'experts et accédez aux projets stratégiques du continent'
    },
    {
      icon: <FaGraduationCap />,
      title: 'Partenaires Académiques',
      description: 'Connectez vos étudiants et chercheurs à des cas d\'usage réels en Afrique'
    },
    {
      icon: <FaBuilding />,
      title: 'Institutions & Gouvernements',
      description: 'Bénéficiez d\'une expertise terrain pour vos stratégies digitales'
    },
    {
      icon: <FaHandshake />,
      title: 'ONG & Organisations',
      description: 'Utilisez la Data et l\'IA pour maximiser l\'impact de vos missions'
    }
  ];

  return (
    <>
      <style>{styles}</style>
      <section className="partner-section">
        <div className="partner-inner">
          <div className="partner-badge">Partenariat</div>
          <h2 className="partner-title">
            L'Afrique ne peut pas attendre.<br />
            Ses talents <em>non plus</em>.
          </h2>
          <p className="partner-subtitle">
            Rejoignez l'écosystème Aifrica et devenez acteur de la transformation digitale du continent africain.
          </p>

          <div className="partner-grid">
            {partnerTypes.map((partner, index) => (
              <div 
                key={index} 
                className="partner-card"
                onClick={handleNavigate}
              >
                <div className="partner-icon">{partner.icon}</div>
                <h3 className="partner-card-title">{partner.title}</h3>
                <p className="partner-card-desc">{partner.description}</p>
              </div>
            ))}
          </div>

          <div className="partner-cta">
            <button 
              className="partner-button"
              onClick={handleNavigate}
            >
              {t('partenaire.cta')}
              <span className="partner-arrow">→</span>
            </button>
          </div>
        </div>
      </section>
    </>
  );
};

export default Partenaire;
